import type { JSX } from 'solid-js'
import Footer from '/web/shared/aifans/Footer'
import { AccountLayout } from '/web/shared/aifans/AccountLayout'
import { IconUser, IconShield } from '/web/shared/aifans/Icons'

import { Component, createEffect, createSignal, onMount, Show } from 'solid-js'

import TextInput from '../../shared/TextInput'
import { getStrictForm, setComponentPageTitle } from '../../shared/util'
import { toastStore, userStore } from '../../store'

export default function UserSettings() {
  const [pass, setPass] = createSignal(false)
  const [saved, setSaved] = createSignal(false)
  let formRef!: HTMLFormElement

  const state = userStore()

  onMount(() => {
    setComponentPageTitle('Settings')
  })

  const submit: JSX.EventHandler<HTMLFormElement, SubmitEvent> = (e: SubmitEvent) => {
    e.preventDefault()
    const body = getStrictForm(formRef, { handle: 'string', email: 'string' })
    userStore.updateProfile({
      handle: body.handle,
      email: body.email,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2400)
  }

  createEffect(() => {
    userStore.getProfile()
    userStore.getConfig()
  })

  const inputClass =
    'w-full rounded-[10px] border border-[rgba(255,255,255,0.07)] bg-[rgba(255,255,255,0.04)] px-4 py-3 font-nunito text-sm text-white outline-none transition-all duration-200 placeholder:text-[rgba(255,255,255,0.28)] focus:border-[rgba(0,229,255,0.5)] focus:bg-[rgba(0,229,255,0.04)] focus:shadow-[0_0_0_3px_rgba(0,229,255,0.12)]'

  const labelClass =
    'mb-1.5 block font-rajdhani text-[11px] font-bold uppercase tracking-[1.4px] text-[rgba(255,255,255,0.55)]'

  const selectClass =
    'w-full appearance-none rounded-[10px] border border-[rgba(255,255,255,0.07)] bg-[rgba(255,255,255,0.04)] px-4 py-3 font-nunito text-sm text-white outline-none transition-all duration-200 focus:border-[rgba(0,229,255,0.5)] focus:shadow-[0_0_0_3px_rgba(0,229,255,0.12)]'

  return (
    <>
      <AccountLayout>
        <div class="mt-3 font-bebas text-[28px] sm:text-[38px] lg:text-[48px] tracking-[1.5px] leading-none text-white mb-4 sm:mt-0 sm:mb-5">Settings</div>
        <div
          class="rounded-2xl border border-[rgba(255,255,255,0.07)] p-6 sm:p-7 lg:p-9"
          style={{ background: 'rgba(16,12,30,0.6)' }}
        >
          <form ref={formRef!} onSubmit={submit} aria-label="Edit profile">
            <TextInput fieldName="userid" value={state.user?._id} disabled class="hidden" />
            <TextInput fieldName="username" value={state.user?.username} disabled class="hidden" />

            {/* PROFILE Section */}
            <div class="mb-8">
              <div class="mb-5 flex items-center gap-3">
                <span
                  class="flex h-8 w-8 items-center justify-center rounded-lg"
                  style={{ background: 'linear-gradient(135deg, rgba(138,43,226,0.2), rgba(0,229,255,0.1))' }}
                >
                  <IconUser size={16} stroke="var(--cf-cyan)" />
                </span>
                <h3 class="font-rajdhani text-lg font-bold uppercase tracking-wider text-white lg:text-[22px]">
                  Profile
                </h3>
              </div>

              <div class="grid gap-5 sm:grid-cols-2">
                <div>
                  <label class={labelClass}>Full Name</label>
                  <input
                    type="text"
                    class={inputClass}
                    placeholder="Your full name"
                    value={state.profile?.handle || ''}
                  />
                </div>
                <div>
                  <label class={labelClass}>Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    class={inputClass}
                    placeholder="you@example.com"
                    value={state.profile?.email || ''}
                    required
                  />
                </div>
                <div>
                  <label class={labelClass}>Username</label>
                  <input
                    type="text"
                    id="handle"
                    name="handle"
                    class={inputClass}
                    placeholder="username"
                    value={state.profile?.handle || ''}
                    required
                  />
                </div>
              </div>
            </div>

            {/* PREFERENCES Section */}
            <div class="mb-8">
              <div class="mb-5 flex items-center gap-3">
                <span
                  class="flex h-8 w-8 items-center justify-center rounded-lg"
                  style={{ background: 'linear-gradient(135deg, rgba(138,43,226,0.2), rgba(0,229,255,0.1))' }}
                >
                  <IconShield size={16} stroke="var(--cf-cyan)" />
                </span>
                <h3 class="font-rajdhani text-lg font-bold uppercase tracking-wider text-white lg:text-[22px]">
                  Preferences
                </h3>
              </div>

              <div class="grid gap-5 sm:grid-cols-2">
                <div>
                  <label class={labelClass}>Content Style</label>
                  <select class={selectClass}>
                    <option value="anime">Anime</option>
                    <option value="comic">Comic</option>
                    <option value="hentai">Hentai</option>
                    <option value="all">All Styles</option>
                  </select>
                </div>
                <div>
                  <label class={labelClass}>Chat Notifications</label>
                  <select class={selectClass}>
                    <option value="all">All Messages</option>
                    <option value="followed">Followed Creators Only</option>
                    <option value="mentions">Mentions Only</option>
                    <option value="off">Off</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div class="flex items-center justify-between">
              <button
                type="button"
                class="rounded-lg border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.04)] px-4 py-2.5 font-rajdhani text-xs font-bold uppercase tracking-wider text-[rgba(255,255,255,0.55)] transition-colors hover:border-[rgba(138,43,226,0.3)] hover:text-white"
                onClick={() => setPass(true)}
              >
                Change Password
              </button>
              <button
                type="submit"
                class="rounded-xl px-6 py-3 font-rajdhani text-sm font-extrabold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-0.5 lg:px-8 lg:py-3.5 lg:text-base"
                style={{
                  background: 'linear-gradient(135deg, #8A2BE2, #FF3CAC)',
                  'box-shadow': '0 6px 24px rgba(138,43,226,0.35)',
                }}
              >
                <Show when={saved()} fallback="Save Changes">
                  ✓ Saved
                </Show>
              </button>
            </div>
          </form>
        </div>
      </AccountLayout>
      <Footer />
      <PasswordModal show={pass()} close={() => setPass(false)} />
    </>
  )
}

const PasswordModal: Component<{ show: boolean; close: () => void }> = (props) => {
  let ref: any
  const save = (e: Event) => {
    e.preventDefault()
    const body = getStrictForm(ref, { newPassword: 'string', confirmPassword: 'string' })
    if (body.newPassword !== body.confirmPassword) {
      toastStore.warn(`Your passwords do not match`)
      return
    }
    userStore.changePassword(body.newPassword, props.close)
  }

  const inputClass =
    'w-full rounded-[10px] border border-[rgba(255,255,255,0.07)] bg-[rgba(255,255,255,0.04)] px-4 py-3 font-nunito text-sm text-white outline-none transition-all duration-200 placeholder:text-[rgba(255,255,255,0.28)] focus:border-[rgba(0,229,255,0.5)] focus:bg-[rgba(0,229,255,0.04)] focus:shadow-[0_0_0_3px_rgba(0,229,255,0.12)]'

  const labelClass =
    'mb-1.5 block font-rajdhani text-[11px] font-bold uppercase tracking-[1.4px] text-[rgba(255,255,255,0.55)]'

  return (
    <Show when={props.show}>
      <div
        class="fixed inset-0 z-[1000] flex items-center justify-center p-5"
        style={{ background: 'rgba(8,6,16,0.78)', 'backdrop-filter': 'blur(10px)' }}
        onClick={(e) => { if (e.target === e.currentTarget) props.close() }}
      >
        <div
          class="relative w-full max-w-[420px] overflow-hidden rounded-2xl border border-[rgba(0,229,255,0.18)] p-8"
          style={{
            background: 'linear-gradient(180deg, #14101f 0%, #0c0916 100%)',
            'box-shadow': '0 30px 80px rgba(138,43,226,0.28), 0 0 0 1px rgba(255,255,255,0.03) inset',
          }}
        >
          {/* Close button */}
          <button
            class="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.05)] text-[rgba(255,255,255,0.55)] transition-all hover:rotate-90 hover:bg-[rgba(255,60,172,0.18)] hover:text-white"
            onClick={props.close}
          >
            ✕
          </button>

          {/* Header */}
          <div class="mb-6 text-center">
            <h2 class="font-bebas text-[28px] tracking-[1px] text-white">Change Password</h2>
            <p class="font-rajdhani text-[13px] font-semibold text-[rgba(255,255,255,0.45)]">
              Enter your new password below
            </p>
          </div>

          {/* Form */}
          <form ref={ref} class="flex flex-col gap-4" onSubmit={save}>
            <div>
              <label class={labelClass}>New Password</label>
              <input
                type="password"
                name="newPassword"
                class={inputClass}
                placeholder="Enter new password"
                required
              />
            </div>
            <div>
              <label class={labelClass}>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                class={inputClass}
                placeholder="Repeat new password"
                required
              />
            </div>

            {/* Actions */}
            <div class="mt-2 flex gap-3">
              <button
                type="button"
                class="flex-1 rounded-xl border border-[rgba(255,255,255,0.08)] bg-transparent py-3 font-rajdhani text-sm font-bold uppercase tracking-wider text-[rgba(255,255,255,0.5)] transition-colors hover:border-[rgba(255,255,255,0.2)] hover:text-white"
                onClick={props.close}
              >
                Cancel
              </button>
              <button
                type="submit"
                class="flex-1 rounded-xl py-3 font-rajdhani text-sm font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, #8A2BE2, #FF3CAC)',
                  'box-shadow': '0 6px 24px rgba(138,43,226,0.35)',
                }}
              >
                Update Password
              </button>
            </div>
          </form>
        </div>
      </div>
    </Show>
  )
}
